import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { 
    format, 
    addDays, 
    isValid, 
    parseISO, 
} from 'date-fns';
import { formatInTimeZone } from "date-fns-tz";
import toast from "react-hot-toast";
import ExcelJS from "exceljs";
import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import { 
    useMediaQuery, 
    useTheme, 
    Box, 
    Typography,  
    CircularProgress, 
    Tooltip, 
    ToggleButton, 
    ToggleButtonGroup,
    IconButton,
} from '@mui/material';
import { 
    FileUpload, 
    ManageAccounts,
    Settings,
    Clear,
    Download,
    Calculate,
    ViewList,
    ViewSidebar,
    ViewModule,
    Save,
    Visibility,
    Check,
    Close,
} from '@mui/icons-material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import useCrewPayrollSave from './useCrewPayrollSave';
import { 
    LAYOUT_HEIGHT, 
    DEFAULT_PIT_CONFIG, 
} from 'state/constants';
import api from 'state/axios';
import { 
    capitalizeFirstLetter,
    safeDifferenceInCalendarDays,
    timeStringToHours, 
    filterCrewMember, 
} from 'state/helpers';
import { fetchGroupedRosterDetailsByCrewCode } from 'state/api';
import { 
    parseDate,
    parseDateTime,
    addNote, 
    getCrewPostBase, 
    getActiveJobTitles,
    checkIsTerminated,
    countDependents,
    countWeekendDays,
    calcActiveDays,
    buildPayables,
    buildHolidayDateSet,
    calcFlyingOnHolidays,
    addDaysCount,
    addFlyingDays,
    calcDutyOnDayOff,    
    accumulateLineInstructorTime,
    determinePaxType,
    accumulatePaxStats,
    accumulateOverStats,
    calcPerdiem,
    mapScheduleCodes,
    calcDutyColumns,
    generateDaysColumns,
    calcIncomeBreakdown,
    calcContributionBreakdown,
    calcVietnamProgressiveTax,
    calcIncomeBeforeTax,
    filterArrayByPeriod,
    toPitCalcFormat,
} from 'state/payroll';
import { logClientAction } from 'state/logger';
import { hasPermission } from 'state/checkPermission';
import Header from 'components/Header';
import NumberControl from 'components/NumberControl';
import ScrollToTop from 'components/ScrollToTop';
import ProgressModal from 'components/ProgressModal';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import FilterButton from 'components/FilterButton';
import CrewFilterDialog from 'components/CrewFilterDialog';
import DataGridComponent from 'components/DataGridComponent';
import RosterDetailsDialog from 'components/RosterDetailsDialog';
import CrewIncomeDialog from "./CrewIncomeDialog";
import UploadConfigDialog from "./UploadConfigDialog";
import PitConfigDialog from "./PitConfigDialog";
import { loadColumnConfig, saveColumnConfig } from "./columnConfig";


const CrewPayroll = () => {
    useAuthCheck();
    const user = useSelector((state) => state.user); 
    const timeMode = useSelector((state) => state.timemode);
    const crewRanksData = useSelector((state) => state.crewRanks); 
    const params = useSelector((state) => state.params);    

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const { startDate, endDate } = useDateRangeContext();

    const canView = hasPermission(user?.permissions, "CrewPayroll", "view", false);
    const canUpload = hasPermission(user?.permissions, "CrewPayroll", "upload", false);
    const canCalculate = hasPermission(user?.permissions, "CrewPayroll", "calculate", false);
    const canSave = hasPermission(user?.permissions, "CrewPayroll", "save", false);

    const { isSaving, saveProgress, handleSave: execSave, handleCancelSave } = useCrewPayrollSave();

    const [loading, setLoading] = useState(false);
    const [isCalculating, setIsCalculating] = useState(false);
    const [calcProgress, setCalcProgress] = useState(null);
    const abortCalculateRef = useRef(false);
    const [error, setError] = useState(null);
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState({ isActive: 'ALL' });
    const defaultFilters = {
        isActive: 'ALL',
    };
    const [openCrewIncomeDialog, setOpenCrewIncomeDialog] = useState(false);
    const [openRosterDetailsDialog, setOpenRosterDetailsDialog] = useState(false);
    const [selectedCrewCode, setSelectedCrewCode] = useState(null);
    const [selectedCrewName, setSelectedCrewName] = useState(null);
    const [crewRosters, setCrewRosters] = useState([]);
    const [crewIncome, setCrewIncome] = useState([]);

    const [fileName, setFileName] = useState("");
    const [rowSelectionModel, setRowSelectionModel] = useState({
        type: 'include',
        ids: new Set(),
    });

    const [view, setView] = useState('rawData');
    const [daysOffset, setDaysOffset] = useState(7);
    const [rawData, setRawData] = useState([]);
    const [payrollDetails, setPayrollDetails] = useState([]);
    const [payrollSummary, setPayrollSummary] = useState([]);
        
    const [pitConfig, setPitConfig] = useState(() => DEFAULT_PIT_CONFIG);
    const [openPitConfig, setOpenPitConfig] = useState(false);
    const [columnConfig, setColumnConfig] = useState(() => loadColumnConfig());
    const [openUploadConfig, setOpenUploadConfig] = useState(false);

    const paramsMap = useMemo(
        () => new Map(params?.map(p => [p.param, p.value]) ?? []),
        [params]
    );
    const [countries, setCountries] = useState([]);
    const [airports, setAirports] = useState([]);
    const [airportPairs, setAirportPairs] = useState([]); 
    const [fixedBlockTimes, setFixedBlockTimes] = useState([]);
    const [holidays, setHolidays] = useState([]);
    const [costItems, setCostItems] = useState([]);
    const [contributions, setContributions] = useState([]);
    const [crewScheduleCodes, setCrewScheduleCodes] = useState([]);
    const [crewMembers, setCrewMembers] = useState([]);
    const [travelAuthorizationForms, setTravelAuthorizationForms] = useState([]);
    const [incomeAdjustments, setIncomeAdjustments] = useState([]);
    const [crewKpis, setCrewKpis] = useState([]);
    const [crewSicks, setCrewSicks] = useState({});
    
    const rankMap = useMemo(
        () => new Map(crewRanksData?.map(r => [r.rankCode, r]) || []),
        [crewRanksData]
    );

    const fcRanks = useMemo(() => {
        return crewRanksData
            ?.filter(r => r.isActive && r.rankType === 'Flight Crew')
            ?.map(r => r.rankCode);
    }, [crewRanksData]);

    const ccRanks = useMemo(() => {
        return crewRanksData
            ?.filter(r => r.isActive && r.rankType === 'Cabin Crew')
            ?.map(r => r.rankCode);
    }, [crewRanksData]);

    
    const fetchCountries = useCallback(() => {
        return api.get('/countries');
    }, []);

    const fetchAirports = useCallback(() => {
        return api.get('/airports/airportswithtravelzone');
    }, []);
    
    const fetchAirportPairs = useCallback(() => {
        return api.get('/airportpairs/sectortype');
    }, []);
    
    const fetchCrewScheduleCodes = useCallback(() => {
        return api.get('/crewschedulecodes');
    }, []);

    const fetchCrewMembers = useCallback(() => {
        return api.get('/crewmembers');
    }, []);

    const fetchFixedBlockTimes = useCallback(() => {
        if (!startDate || !endDate) return [];
        const payload = { startDate, endDate }; 
        return api.post('/fixedblocktimes', payload);
    }, [startDate, endDate]);
    
    const fetchHolidays = useCallback(() => {
        if (!startDate || !endDate) return [];
        const payload = { startDate, endDate }; 
        return api.post('/holidays/withinperiod', payload);
    }, [startDate, endDate]);
    
    const fetchCostItems = useCallback(() => {       
        if (!startDate || !endDate) return [];
        const payload = { startDate, endDate }; 
        return api.post('/costitems/active', payload);
    }, [startDate, endDate]);
    
    const fetchContributions = useCallback(() => {   
        if (!startDate || !endDate) return [];
        const payload = { startDate, endDate };     
        return api.post('/contributions/active', payload);
    }, [startDate, endDate]);
        
    const fetchTravelAuthorizationForms = useCallback(() => {
        if (!startDate || !endDate) return [];
        const payload = { startDate, endDate };        
        return api.post('/travelauthorizationforms', 
            payload
        );
    }, [startDate, endDate]);

    const fetchIncomeAdjustments = useCallback(() => {
        if (!startDate || !endDate) return [];
        const payload = { startDate, endDate };
        return api.post('/incomeadjustments', 
            payload
        );
    }, [startDate, endDate]);

    const fetchCrewKpis = useCallback(() => {
        if (!startDate || !endDate) return [];
        const payload = { startDate, endDate };
        return api.post('/crewkpis', 
            payload
        );
    }, [startDate, endDate]);

    const fetchCrewSicks = useCallback(() => {
        if (!startDate || !endDate) return {};
        const payload = { startDate, endDate };
        return api.post('/crewpayroll/crewsick', 
            payload
        );
    }, [startDate, endDate]);
        
    const fetchPitConfig = useCallback(() => {
        const date = startDate 
            ? new Date(startDate).toISOString().slice(0, 10) 
            : new Date().toISOString().slice(0, 10);
            
        return api.get(`/pitconfig/active?date=${date}`);
    }, [startDate]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setError(null);
                setLoading(true);

                const [
                    countriesResponse, 
                    airportsResponse, 
                    airportPairsResponse, 
                    crewScheduleCodesResponse, 
                    crewMembersResponse,
                    fixedBlockTimesResponse,
                    holidaysResponse,
                    costItemsResponse,
                    contributionsResponse,
                    taFormsResponse,
                    incomeAdjustmentsResponse,
                    crewKpisResponse,
                    crewSicksResponse,
                    pitConfigResponse,
                ] = await Promise.all([
                    fetchCountries(),
                    fetchAirports(),
                    fetchAirportPairs(),
                    fetchCrewScheduleCodes(), 
                    fetchCrewMembers(),
                    fetchFixedBlockTimes(), 
                    fetchHolidays(),
                    fetchCostItems(),
                    fetchContributions(),
                    fetchTravelAuthorizationForms(),
                    fetchIncomeAdjustments(),
                    fetchCrewKpis(),
                    fetchCrewSicks(),
                    fetchPitConfig().catch(() => null),
                ]);
                
                setCountries(countriesResponse.data);
                setAirports(airportsResponse.data);
                setAirportPairs(airportPairsResponse.data);
                setCrewScheduleCodes(crewScheduleCodesResponse.data);
                setCrewMembers(crewMembersResponse.data);
                setFixedBlockTimes(fixedBlockTimesResponse.data);
                setHolidays(holidaysResponse.data);
                setCostItems(costItemsResponse.data);
                setContributions(contributionsResponse.data);
                setTravelAuthorizationForms(taFormsResponse.data);
                setIncomeAdjustments(incomeAdjustmentsResponse.data);
                setCrewKpis(crewKpisResponse.data);
                setCrewSicks(crewSicksResponse?.data?.data || {});
                if (pitConfigResponse?.data) {
                    setPitConfig(toPitCalcFormat(pitConfigResponse.data) || DEFAULT_PIT_CONFIG);
                }
            } catch (error) {
                toast.error(error.response?.data?.message || "Failed to fetch data!");
                setError(error);
            } finally {
                setLoading(false);
            }
        };
    
        fetchData();
    }, [
        fetchCountries, 
        fetchAirports, 
        fetchAirportPairs,
        fetchCrewScheduleCodes, 
        fetchCrewMembers,
        fetchFixedBlockTimes, 
        fetchHolidays,
        fetchCostItems,
        fetchContributions,
        fetchTravelAuthorizationForms,
        fetchIncomeAdjustments,
        fetchCrewKpis,
        fetchCrewSicks,
        fetchPitConfig,
    ]);    
    
    const handleCrewPayroll = useCallback(async () => {
        if (!startDate || !endDate) return [];

        try {
            setError(null);
            setLoading(true);

            const url = '/crewpayroll';
            const payload = { startDate, endDate, daysOffset };
            const response = await api.post(url, payload);

            const { timesheetDetails, timesheetSummary } = response.data;
            
            setPayrollDetails(timesheetDetails);
            setPayrollSummary(timesheetSummary);

            setView('payrollSummary');
        } catch (error) {
            setError(error);
            toast.error(error.response?.data?.message || "Failed to fetch crew payroll!");
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, daysOffset]);

    
    const filteredPayroll = useMemo(() => {
        let payrollData;
        if (view === 'rawData') {            
            payrollData = rawData;
        } else if (view === 'payrollDetails') {
            payrollData = payrollDetails;
        } else {
            payrollData = payrollSummary;
        }

        if (!Array.isArray(payrollData)) return [];
        if (!filters) return payrollData;

        return payrollData.filter(member => filterCrewMember(member, filters));
    }, [
        rawData, 
        payrollDetails, 
        payrollSummary, 
        view, 
        filters
    ]);            
    
    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);
      
    const handleFilterApply = (filterValues) => {
        setFilters(filterValues); 
    };    
            
    const handleCloseRosterDetailsDialog = () => {
        setCrewRosters([]);
        setOpenRosterDetailsDialog(false);
    };
    
    const handleViewCrewRosters = useCallback(async (param) => {
        if (!startDate || !endDate) return [];
        
        try {
            const crewCode = param.crewCode || "";
            const crewName = param.crewName || "";
            setSelectedCrewCode(crewCode);
            setSelectedCrewName(crewName);

            const result = await fetchGroupedRosterDetailsByCrewCode(crewCode, startDate, endDate);
            
            setCrewRosters(result);
            setOpenRosterDetailsDialog(true);
        } catch (error) {
            setOpenRosterDetailsDialog(false);
            toast.error(error?.message || "Failed to fetch roster details.");
        } 
    }, [startDate, endDate]); 
        
    const handleSaveColumnConfig = useCallback((newConfig) => {
        saveColumnConfig(newConfig);
        setColumnConfig(newConfig);
    }, []);

    const handleSavePitConfig = useCallback(async (payload) => {
        try {
            const res = await api.put('/pitconfig', payload);
            setPitConfig(toPitCalcFormat(res.data));
            toast.success('PIT configuration saved successfully.');
            setOpenPitConfig(false);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save PIT configuration.');
        }
    }, []);

    const handleRowUpdate = (newRow, oldRow) => {
        try {
            if (view === 'payrollSummary') {
                setPayrollSummary((prev) =>
                    prev.map((row) =>
                        row._id === newRow._id ? { ...row, ...newRow } : row
                    )
                );
            } else {
                setPayrollDetails((prev) =>
                    prev.map((row) =>
                        row._id === newRow._id ? { ...row, ...newRow } : row
                    )
                );
            }
            return newRow; // DataGrid sẽ dùng row này để render tiếp
        } catch (error) {
            toast.error("Update failed, reverting row...");
            return oldRow; // rollback về row cũ nếu lỗi
        }
    };

    const handleRowUpdateError = (error) => {
        toast.error(error?.message || "Error updating row!");
    };

    const handleDeleteRows = useCallback(() => {
        if (rowSelectionModel.ids?.size === 0) {
            toast.error('No rows selected for deletion!');
            return;
        }

        if (view === 'payrollSummary') {
            setPayrollSummary((prevRows) =>
                prevRows.filter((row) => !rowSelectionModel.ids.has(row._id))
            );
        } else {
            setPayrollDetails((prevRows) =>
                prevRows.filter((row) => !rowSelectionModel.ids.has(row._id))
            );
        };
        
        setRowSelectionModel({ type: 'include', ids: new Set() });
        toast.success('Selected rows deleted successfully!');
    }, [rowSelectionModel, view]);
    
    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Delete') {
            event.preventDefault(); 
            handleDeleteRows();
        }
    }, [handleDeleteRows]);

    const handleJob147Upload = useCallback((event) => {
        const file = event.target.files?.[0];

        if (!file) return;
        if (!file.name?.toLowerCase().endsWith('.txt')) {
            toast.error("Please upload a .txt file.");
            return;
        }

        setFileName(file.name);
        setLoading(true);

        // const maxIndex = Math.max(...columnConfig.map(c => c.index));
        const enabledColumns = columnConfig.filter(c => c.enabled);

        Papa.parse(file, {
            skipEmptyLines: true,
            complete: (results) => {
                const validRows = results.data
                    .filter(row => row.length === enabledColumns.length)
                    .map((row, index) => ({
                        _id: index,
                        ...row,
                        // ...Object.fromEntries(
                        //     columnConfig.filter(e => e.enabled).map(e => [e.fieldName, row[e.index] ?? ''])
                        // ),
                    }));
                    
              
                setRawData(validRows);
                setPayrollDetails([]);
                setPayrollSummary([]);
                setView('rawData');
                setLoading(false);
                toast.success(`Loaded ${validRows.length} rows. Click Calculate to process.`);
            },
            error: (err) => { 
                toast.error(err.message); 
                setLoading(false); 
            },
        });

        event.target.value = '';
    }, [columnConfig]);

    const handleResetFile = useCallback(() => {
        setFileName('');
        setRawData([]);
        setPayrollDetails([]);
        setPayrollSummary([]);
        setView('rawData');
        setRowSelectionModel({ type: 'include', ids: new Set() });
    }, []);

    const handleCalculate = useCallback(async () => {
        if (!rawData.length) {
            toast.error('No raw data to process. Please upload a file first.');
            return;
        }

        abortCalculateRef.current = false;
        setCalcProgress(null);
        setIsCalculating(true);
        setError(null);

        await new Promise(resolve => setTimeout(resolve, 50));

        try {
            const periodStart = addDays(startDate, 0);
            const periodEnd = addDays(endDate, 1);
            const totalDays = safeDifferenceInCalendarDays(endDate, startDate) + 1;

            const personalDeduction_Unit = pitConfig.personalDeduction ?? 15500000;
            const dependentDeduction_Unit = pitConfig.dependentDeduction ??  6200000;

            const airportMap = new Map();
            airports?.forEach(ap => { airportMap.set(ap.iataCode, ap); });

            const travelZoneSet = new Set();
            airports?.forEach(ap => { 
                const tz = ap.travelZone; 
                if (tz) travelZoneSet.add(tz); 
            });

            const domesticCountrySet = new Set(countries.filter(country => country.isDefault)?.map(country => country.countryCode));
            const domesticAirports = airports.filter(airport => domesticCountrySet.has(airport.countryCode));
            const domesticAirportSet = new Set(domesticAirports?.map(airport => airport.iataCode));

            const airportPairMap = new Map(airportPairs?.map(ap => [ap.sector, ap.sectorType]));

            const fixedBlockTimeMap = new Map();
            for (const fbt of fixedBlockTimes) {
                const acTypeList = fbt.acTypes
                    ? fbt.acTypes
                        .split(/[;,]/) // split theo , hoặc ;
                        .map(type => type.trim())
                        .filter(Boolean)
                    : [];

                if (acTypeList.length === 0) {
                    fixedBlockTimeMap.set(`${fbt.sector}`, fbt.blockTime);
                    continue;
                }
                for (const acType of acTypeList) {
                    fixedBlockTimeMap.set(`${fbt.sector}-${acType}`, fbt.blockTime);
                }
            }

            const contributionMap = new Map(
                contributions?.map(i => [
                    i.contributionObject, 
                    { 
                        effectiveFrom: i.effectiveFrom,
                        effectiveTo: i.effectiveTo,
                        contributionRate: i.contributionRate, 
                    }
                ])
            );

            const crewScheduleCodeMap = new Map(
                crewScheduleCodes?.map(c => [
                    c.code, 
                    { 
                        category: c.category, 
                        isPaid: c.isPaid, 
                        isTimesheet: c.isTimesheet, 
                        workingDays: c.workingDays,
                    }
                ])
            );

            const travelAllowanceByCode = travelAuthorizationForms?.reduce((acc, cur) => {
                acc[cur.crewCode] = (acc[cur.crewCode] || 0) + (cur.allowanceAmount || 0);
                return acc;
            }, {});
            const travelAuthorizationFormMap = new Map(
                Object.entries(travelAllowanceByCode)?.map(([crewCode, allowanceAmount]) => [crewCode, { allowanceAmount }])
            );

            const incomeAdjustmentMap = new Map();
            for (const adjustment of incomeAdjustments) {
                const { amount = 0, adjustmentType, isNonTaxableIncome, crewMembers = [] } = adjustment;
                for (const member of crewMembers) {
                    if (!member?.crewCode) continue;
                    const key = String(member.crewCode);
                    const prev = incomeAdjustmentMap.get(key) || { amount: 0, types: [], isNonTaxableIncome: false };
                    incomeAdjustmentMap.set(key, {
                        amount: prev.amount + amount,
                        types: [...new Set([...prev.types, adjustmentType])],
                        isNonTaxableIncome: prev.isNonTaxableIncome || isNonTaxableIncome,
                    });
                }
            }

            const kpiValues = { A: { kpiRate: 1.0 }, B: { kpiRate: 0.7 }, C: { kpiRate: 0.0 } };
            const crewKpiMap = new Map(
                (crewKpis?.crewKpi ?? []).map(c => [c.crewCode, { kpi: c.kpi, kpiRate: kpiValues[c.kpi]?.kpiRate ?? 0 }])
            );

            const crewMemberMap = new Map();
            crewMembers?.forEach(member => {
                const key = member.crewCode;
                const activeJobs = getActiveJobTitles(member.jobTitles, startDate, endDate);
                const activeJobTitles = activeJobs.flatMap(jt => jt.jobTitle);
                const payables = buildPayables(costItems, activeJobTitles);
                const isTerminated = checkIsTerminated(member.employmentEndDate, endDate);
                crewMemberMap.set(key, { ...member, isTerminated, activeJobs, activeJobTitles, payables });
            });

            const holidayDateSet = buildHolidayDateSet(holidays, startDate, endDate);
            

            const CREW_PAYROLL_TIME_MODE = paramsMap.get("CREW_PAYROLL_TIME_MODE") || "UTC";
            const CREW_PAYROLL_PAX_TIME = paramsMap.get("CREW_PAYROLL_PAX_TIME") || "FBT"; 
            // const TIMEZONE = paramsMap.get("TIMEZONE") || 'Asia/Ho_Chi_Minh';
            const OVER_TIME_MINUTES = Number(paramsMap.get("OVER_TIME_MINUTES")) || 20;
            const MINIMUM_PERDIEM_MINUTES = Number(paramsMap.get("MINIMUM_PERDIEM_MINUTES")) || 360;
            const MAXIMUM_PERDIEM_MINUTES = Number(paramsMap.get("MAXIMUM_PERDIEM_MINUTES")) || 7200;
            const PER_DIEM_CALC_METHOD = paramsMap.get("PER_DIEM_CALC_METHOD") || "HOURLY";
            const UNPAID_CODES = paramsMap.get("UNPAID_CODES")?.split(',').map(item => item.trim()) || ['O','o'];
            const INSTRUCTOR_LINE_TRAINING_CODES = paramsMap.get("INSTRUCTOR_LINE_TRAINING_CODES")?.split(',')?.map(item => item.trim());
            const TRAINEE_LINE_TRAINING_CODES = paramsMap.get("TRAINEE_LINE_TRAINING_CODES")?.split(',')?.map(item => item.trim());
            const TRAINEE_LINE_UPGRADING_CODES = paramsMap.get("TRAINEE_LINE_UPGRADING_CODES")?.split(',')?.map(item => item.trim()) || 'u';
            const INSTRUCTOR_SIMULATOR_CODES = paramsMap.get("INSTRUCTOR_SIMULATOR_CODES")?.split(',')?.map(item => item.trim());
            const INSTRUCTOR_GROUND_TRAINING_CODES = paramsMap.get("INSTRUCTOR_GROUND_TRAINING_CODES")?.split(',')?.map(item => item.trim());
            const INTERNAL_AUDIT_CODES = paramsMap.get("INTERNAL_AUDIT_CODES")?.split(',')?.map(item => item.trim());
            const INCENTIVE_CODES = paramsMap.get("INCENTIVE_CODES")?.split(',')?.map(item => item.trim());
            // const DOM_REPORTING_MINUTES = Number(paramsMap.get("DOM_REPORTING_MINUTES")) || 60;
            // const INT_REPORTING_MINUTES = Number(paramsMap.get("INT_REPORTING_MINUTES")) || 75;
            const DEBRIEFING_MINUTES = Number(paramsMap.get("DEBRIEFING_MINUTES")) || 15;
            // const MINIMUM_CREW_REST_MINUTES = Number(paramsMap.get("MINIMUM_CREW_REST_MINUTES")) || 600;
            // const POSITIONING_CODE = paramsMap.get("POSITIONING_CODE") || 'DHC';
            const POSITIONING_RULE = paramsMap.get("POSITIONING_RULE") || "AFB";
            const MINIMUM_TAT_MINUTES = Number(paramsMap.get("MINIMUM_TAT_MINUTES")) || 10;
            const MINIMUM_OVERLAP_WORKDAY_MINUTES = Number(paramsMap.get("MINIMUM_OVERLAP_WORKDAY_MINUTES")) || 120;
            const MINIMUM_INSURANCE_DAYS = Number(paramsMap.get("MINIMUM_INSURANCE_DAYS")) || 14;
            const MAXIMUM_PAYABLE_SICK_DAYS = Number(paramsMap.get("MAXIMUM_PAYABLE_SICK_DAYS")) || 6;
            const FLIGHT_CREW_EXCESS_BLOCK_HOURS = Number(paramsMap.get("FLIGHT_CREW_EXCESS_BLOCK_HOURS")) || 75;
            const CABIN_CREW_EXCESS_BLOCK_HOURS = Number(paramsMap.get("CABIN_CREW_EXCESS_BLOCK_HOURS")) || 83;

            let atdDate = "atdUTCDate";
            let ataDate = "ataUTCDate";
            if (CREW_PAYROLL_TIME_MODE === "LCL") {    
                atdDate = "atdLCLDate";
                ataDate = "ataLCLDate";
            }
            
            const enabledFieldIndexes = columnConfig
                .filter(c => c.enabled)
                .map(c => [c.fieldName, c.index]);
            const fieldIndexesMap = Object.fromEntries(enabledFieldIndexes);

            let { cleanedData, tripTypeCodeSet } = rawData?.reduce(
                (acc, row) => {
                    const tripTypeCode = row[fieldIndexesMap.tripTypeCode];
                    const dutyDate = row[fieldIndexesMap.dutyDate];
                    const acType = row[fieldIndexesMap.acType];
                    const flightNo = row[fieldIndexesMap.flightNo];

                    if (!tripTypeCode || (acType && !flightNo)) return acc;

                    if (tripTypeCode) {                        
                        const prev = acc.cleanedData[acc.cleanedData.length - 1];
                        const isRedundant = prev 
                            && tripTypeCode === prev[fieldIndexesMap.tripTypeCode]
                            && dutyDate === prev[fieldIndexesMap.dutyDate]
                            && !flightNo
                            && row[fieldIndexesMap.dutyReportDateUTC] === prev[fieldIndexesMap.dutyDebriefDateUTC]
                            && !row[fieldIndexesMap.dutyReportTimeUTC] 
                            && prev[fieldIndexesMap.dutyReportTimeUTC]

                        if (!isRedundant) {
                            acc.cleanedData.push(row);
                        }

                        if (!flightNo && crewScheduleCodeMap.has(tripTypeCode)) {
                            acc.tripTypeCodeSet.add(tripTypeCode);
                        }
                    }

                    return acc;
                },
                { cleanedData: [], tripTypeCodeSet: new Set() }
            );

            const tripTypeCodes = Array.from(tripTypeCodeSet)
                .sort((a, b) =>
                    (crewScheduleCodeMap.get(a)?.category || "")
                        .localeCompare(crewScheduleCodeMap.get(b)?.category || "")
                );
                
            const transformedData = cleanedData?.map((row, rowIndex) => {
                const baseData = {};
                for (const [field, colIndex] of enabledFieldIndexes) {
                    baseData[field] = row[colIndex];
                }

                const {
                    crewCode,
                    post,
                    // base,
                    dutyDate,
                    tripTypeCode,
                    dutyReportDateUTC,
                    dutyReportTimeUTC,
                    dutyReportDateLCL,
                    dutyReportTimeLCL,
                    dutyDebriefDateUTC,
                    dutyDebriefTimeUTC,
                    dutyDebriefDateLCL,
                    dutyDebriefTimeLCL,
                    pax,
                    // tailNumber,
                    acType,
                    flightNo,
                    // legSequenceNumber,
                    // postOnFlight,
                    departureAirport,
                    scheduledDateOfDepartureUTC,
                    scheduledTimeOfDepartureUTC,
                    scheduledDateOfDepartureLCL,
                    scheduledTimeOfDepartureLCL,
                    actualDateOfDepartureUTC,
                    actualTimeOfDepartureUTC,
                    actualDateOfDepartureLCL,
                    actualTimeOfDepartureLCL,
                    arrivalAirport,
                    scheduledDateOfArrivalUTC,
                    scheduledTimeOfArrivalUTC,
                    scheduledDateOfArrivalLCL,
                    scheduledTimeOfArrivalLCL,
                    actualDateOfArrivalUTC,
                    actualTimeOfArrivalUTC,
                    actualDateOfArrivalLCL,
                    actualTimeOfArrivalLCL,
                    // scheduledBlockTime,
                    // actualBlockTime,
                    fdpStartDateUTC,
                    fdpStartTimeUTC,
                    fdpStartDateLCL,
                    fdpStartTimeLCL,
                    fdpEndDateUTC,
                    fdpEndTimeUTC,
                    fdpEndDateLCL,
                    fdpEndTimeLCL,
                    // layoverTimeForThisDay,
                    trainingDutyIndicator,
                    // tafbForThisDay,
                    specialActivityOnTheDay,
                    paidDayOffIndicator,
                    // creditHoursOfScheduleCode,
                    // lineNumber,
                    // tafbOffset,
                    // crewName,
                    // crewSurname,
                    // crewFirstname,
                    // crewMiddleName,
                    // crew3LetterCode,
                    // crewCategory,
                    dutyTime,
                    // creditTime,
                    domesticBlockTime,
                    internationalBlockTime,
                    // domesticLayoverTime,
                    // internationalLayoverTime,
                    // nightHoursOfThePairing,
                    // noOfLandings,
                    // noOfTakeOffs,
                    // tafbDom,
                    // tafbInt,
                    // manualOrAutoLanding,
                    // manualOrAutoTakeOff
                } = baseData;
                
                const cm = crewMemberMap.get(crewCode);
                if (!cm) return null;

                const flightDate = parseDate(scheduledDateOfDepartureUTC || dutyDate);
                
                const crewPostBase = getCrewPostBase(cm?.crewQuals, crewRanksData, flightDate)
                const crewRank = cm?.rank === "IOE"
                    ? "IOE"
                    : crewPostBase?.post ?? cm?.rank ?? null;
                const crewBase = crewPostBase?.base || cm?.location || null;
                
                const activeJobs = cm?.activeJobs || [];            
                const jobTitles = getActiveJobTitles(cm?.jobTitles, flightDate);
                const jobTitle = jobTitles?.map(j => Array.isArray(j.jobTitle) ? j.jobTitle.join(", ") : j.jobTitle).join(", ") || "";
                
                const contributionObject = cm?.contributionObject || '';
              
                const sector = flightNo && departureAirport && arrivalAirport && (domesticBlockTime || internationalBlockTime)
                    ? `${departureAirport}-${arrivalAirport}` 
                    : null;
                                            
                const reportUTCDate = parseDateTime(dutyReportDateUTC, dutyReportTimeUTC);
                const debriefUTCDate = parseDateTime(dutyDebriefDateUTC, dutyDebriefTimeUTC);
                const reportLCLDate = parseDateTime(dutyReportDateLCL, dutyReportTimeLCL);
                const debriefLCLDate = parseDateTime(dutyDebriefDateLCL, dutyDebriefTimeLCL);
                
                const fdpStartUTCDate = parseDateTime(fdpStartDateUTC, fdpStartTimeUTC);
                const fdpEndUTCDate = parseDateTime(fdpEndDateUTC, fdpEndTimeUTC);
                const fdpStartLCLDate = parseDateTime(fdpStartDateLCL, fdpStartTimeLCL);
                const fdpEndLCLDate = parseDateTime(fdpEndDateLCL, fdpEndTimeLCL);

                let atdUTCDate = null, ataUTCDate = null;
                let atdLCLDate = null, ataLCLDate = null;

                const domesticBlockHours = timeStringToHours(domesticBlockTime);
                const internationalBlockHours = timeStringToHours(internationalBlockTime);

                let sectorType = null;                  

                if (sector) { // Flight
                    atdUTCDate = parseDateTime(actualDateOfDepartureUTC, actualTimeOfDepartureUTC);                        
                    ataUTCDate = parseDateTime(actualDateOfArrivalUTC, actualTimeOfArrivalUTC);                           
                    atdLCLDate = parseDateTime(actualDateOfDepartureLCL, actualTimeOfDepartureLCL);                        
                    ataLCLDate = parseDateTime(actualDateOfArrivalLCL, actualTimeOfArrivalLCL);
                    
                    if (domesticBlockHours > 0) {
                        sectorType = "Domestic";
                    } else if (internationalBlockHours > 0) {
                        sectorType = "International";
                    }
                } else { // Ground
                    atdUTCDate = ataUTCDate = parseDate(dutyDate);
                    if (scheduledDateOfDepartureUTC && scheduledTimeOfDepartureUTC) {
                        atdUTCDate = parseDateTime(scheduledDateOfDepartureUTC, scheduledTimeOfDepartureUTC);
                    } 
                    if (scheduledDateOfArrivalUTC && scheduledTimeOfArrivalUTC) {
                        ataUTCDate = parseDateTime(scheduledDateOfArrivalUTC, scheduledTimeOfArrivalUTC);
                    } 

                    atdLCLDate = ataLCLDate = parseDate(dutyDate);
                    if (scheduledDateOfDepartureLCL && scheduledTimeOfDepartureLCL) {
                        atdLCLDate = parseDateTime(scheduledDateOfDepartureLCL, scheduledTimeOfDepartureLCL);
                    } 
                    if (scheduledDateOfArrivalLCL && scheduledTimeOfArrivalLCL) {
                        ataLCLDate = parseDateTime(scheduledDateOfArrivalLCL, scheduledTimeOfArrivalLCL);
                    } 
                }

                const blockTime = domesticBlockHours > 0 
                    ? domesticBlockHours 
                    : internationalBlockHours;

                const fixedBlockTime = 
                    fixedBlockTimeMap.get(`${sector}-${acType}`) ??
                    fixedBlockTimeMap.get(sector) ??
                    0;

                    
                let perdiem = 0, domPerdiem = 0, intPerdiem = 0;
                const travelZoneSet_Perdiem = {};
                [...travelZoneSet]
                    .sort((a, b) => a.localeCompare(b))
                    .forEach(tz => {
                        travelZoneSet_Perdiem[`${tz}_Perdiem`] = 0;
                    });
                const depTravelZone = sector ? airportMap.get(departureAirport)?.travelZone : null;
                const arrTravelZone = sector ? airportMap.get(arrivalAirport)?.travelZone : null;
                
                let paxType = null;
                let paxTime = 0;
                let paxSectors = 0;
                let payableBlockTime = 0;
                let payableSectors = 0;
                let unpaidBlockTime = 0;
                let unpaidSectors = 0;

                let convertedBlockTime = fixedBlockTime || blockTime || 0; // Round up to 6 minutes (0.1 hour) for BAV payroll rules
                // let overTime = 0;
                // let convertedOverTime = 0;

                let isFlyingOnHolidays = false;

                if (sector) {                        
                    if (!pax) {
                        const isUnpaid = UNPAID_CODES?.includes(trainingDutyIndicator);
                        const otThreshold = OVER_TIME_MINUTES / 60;

                        if (fixedBlockTime) {
                            if ((blockTime - fixedBlockTime) > otThreshold) {
                                convertedBlockTime = Math.ceil(blockTime * 10) / 10;                          
                                // overTime = blockTime - fixedBlockTime;
                                // convertedOverTime = convertedBlockTime - fixedBlockTime;
                            }
                        } else {
                            convertedBlockTime = Math.ceil(blockTime * 10) / 10;
                        }

                        if (!isUnpaid) {
                            payableBlockTime = convertedBlockTime;
                            payableSectors = 1;
                        } else {
                            unpaidBlockTime = convertedBlockTime;
                            unpaidSectors = 1;
                        }                    
                        
                        // Check if flying on holidays
                        const _rowAtd = CREW_PAYROLL_TIME_MODE === "UTC" ? atdUTCDate : atdLCLDate;
                        const _rowAta = CREW_PAYROLL_TIME_MODE === "UTC" ? ataUTCDate : ataLCLDate;
                        if (_rowAtd) {
                            const _atdStr = formatInTimeZone(new Date(_rowAtd), 'UTC', 'yyyy-MM-dd');
                            if (holidayDateSet.has(_atdStr)) isFlyingOnHolidays = true;
                        }
                        if (!isFlyingOnHolidays && _rowAta) {
                            const _ataStr = formatInTimeZone(new Date(_rowAta), 'UTC', 'yyyy-MM-dd');
                            if (holidayDateSet.has(_ataStr)) isFlyingOnHolidays = true;
                        }   
                    } else {
                        paxTime = convertedBlockTime; // CREW_PAYROLL_PAX_TIME === "FBT"
                        if (CREW_PAYROLL_PAX_TIME === "ABT") {
                            paxTime = blockTime;
                        }
                        paxSectors = 1;
                    }
                }        

                const bhByTypes = {};
                const sectorByTypes = {};
                for (const sectorType of new Set([...airportPairMap.values()].sort())) {
                    bhByTypes[`bhType_${sectorType}`] = 0;
                    sectorByTypes[`sectorType_${sectorType}`] = 0;
                }
                
                const pairType = airportPairMap.get(sector);
                const effectiveType = pairType || 'Default';
                bhByTypes[`bhType_${effectiveType}`] = payableBlockTime;
                sectorByTypes[`sectorType_${effectiveType}`] = payableSectors;
                

                let lineInstructorTime = 0; 
                let lineInstructorSectors = 0;
                if (trainingDutyIndicator && INSTRUCTOR_LINE_TRAINING_CODES?.includes(trainingDutyIndicator)) {
                    lineInstructorTime = payableBlockTime;
                    lineInstructorSectors = 1;
                }
                                
                let lineTraineeTime = 0;
                let lineTraineeSectors = 0;
                if (trainingDutyIndicator && TRAINEE_LINE_TRAINING_CODES?.includes(trainingDutyIndicator)) {
                    lineTraineeTime = payableBlockTime;
                    lineTraineeSectors = 1;
                }

                let lineUpgradingTraineeTime = 0;
                let lineUpgradingTraineeSectors = 0;
                if (trainingDutyIndicator && TRAINEE_LINE_UPGRADING_CODES?.includes(trainingDutyIndicator)) {
                    lineUpgradingTraineeTime = payableBlockTime;
                    lineUpgradingTraineeSectors = 1;
                }

                let internalAuditTime = 0;
                let internalAuditSectors = 0;
                if (trainingDutyIndicator && INTERNAL_AUDIT_CODES?.includes(trainingDutyIndicator)) {
                    internalAuditTime = payableBlockTime;
                    internalAuditSectors = 1;
                } 

                const dutyHours = timeStringToHours(dutyTime);
                // const creditHours = timeStringToHours(creditHoursOfScheduleCode);
                
                let simInstructorTime = 0;
                if (!flightNo && tripTypeCode && INSTRUCTOR_SIMULATOR_CODES?.includes(tripTypeCode)) {
                    // simInstructorTime = creditHours > 0 
                    //     ? creditHours 
                    //     : dutyHours;
                    simInstructorTime = dutyHours;
                }       
                
                let groundInstructorTime = 0;
                if (!flightNo && tripTypeCode && INSTRUCTOR_GROUND_TRAINING_CODES?.includes(tripTypeCode)) {
                    // groundInstructorTime = creditHours > 0 
                    //     ? creditHours 
                    //     : dutyHours;
                    groundInstructorTime = dutyHours;
                } 
                
                let incentiveTime = 0;
                let incentiveSectors = 0;
                if (specialActivityOnTheDay && INCENTIVE_CODES?.includes(specialActivityOnTheDay)) {
                    incentiveTime = payableBlockTime;
                    incentiveSectors = 1;
                }

                const paidDayOffs = paidDayOffIndicator ? 1 : 0;                                                
                
                const unpaidDays = crewScheduleCodeMap.has(tripTypeCode) && !crewScheduleCodeMap.get(tripTypeCode).isPaid ? 1 : 0;
                // const timesheetCodes = crewScheduleCodeMap.has(tripTypeCode) && crewScheduleCodeMap.get(tripTypeCode).isTimesheet ? 1 : 0;
                let note = '';

                const tripTypeColumns = tripTypeCodes.reduce((acc, code) => {
                    acc[code] = tripTypeCode === code ? 1 : null;
                    return acc;
                }, {});

                return {
                    _id: rowIndex,              //0
                    ...baseData,      

                    crewRank: crewRank || post, //1
                    crewBase,                   //2
                    activeJobs,                 //3
                    jobTitle,                   //4
                    contributionObject,         //5
                    
                    reportUTCDate,              //6
                    debriefUTCDate,             //7
                    reportLCLDate,              //8
                    debriefLCLDate,             //9

                    fdpStartUTCDate,            //10
                    fdpEndUTCDate,              //11
                    fdpStartLCLDate,            //12
                    fdpEndLCLDate,              //13

                    atdUTCDate,                 //14
                    ataUTCDate,                 //15
                    atdLCLDate,                 //16
                    ataLCLDate,                 //17
                    
                    sector,                     //18
                    sectorType,                 //19
                    depTravelZone,              //20
                    arrTravelZone,              //21

                    note,                       //22
                    isFlyingOnHolidays,         //23

                    paxType,                    //24   
                    paxTime,                    //25
                    paxSectors,                 
                    ...bhByTypes,
                    ...sectorByTypes,
                    blockTime,
                    // fixedBlockTime,
                    // overTime,
                    convertedBlockTime,
                    // convertedOverTime,
                    unpaidBlockTime,
                    unpaidSectors,      
                    payableBlockTime,
                    payableSectors,                                            
                    lineInstructorTime,
                    lineInstructorSectors,
                    internalAuditTime,       
                    internalAuditSectors,             
                    simInstructorTime,
                    groundInstructorTime,
                    incentiveTime,
                    incentiveSectors,
                    lineTraineeTime,
                    lineTraineeSectors,
                    lineUpgradingTraineeTime,
                    lineUpgradingTraineeSectors,
                    paidDayOffs,
                    unpaidDays,
                    perdiem,
                    domPerdiem,
                    intPerdiem,
                    ...travelZoneSet_Perdiem,
                    ...tripTypeColumns,
                    // timesheetCodes,
                };
            }).filter((row) => row !== null);               
            

            // Map rosters by crewCode & Create crew member list
            const rostersByCrewMap = new Map();
            const crewMap = new Map();

            for (const row of transformedData) {
                // Map rosters by crewCode
                if (!rostersByCrewMap.has(row.crewCode)) {
                    rostersByCrewMap.set(row.crewCode, []);
                }
                rostersByCrewMap.get(row.crewCode).push(row);

                // Create crew member list
                if (!crewMap.has(row.crewCode)) {
                    crewMap.set(row.crewCode, {
                        crewCode: row.crewCode,
                        crewName: row.crewName,
                        crewRank: row.crewRank,
                        crewBase: row.crewBase,
                        activeJobs: row.activeJobs,
                        contributionObject: row.contributionObject,
                    });
                }
            }
            
            const crewSets = Array.from(crewMap.values());
            
            const processTimesheet = async () => {
                const timesheetDetails = [];
                const timesheetSummary = [];
                
                const perdiemConfig = {
                    PER_DIEM_CALC_METHOD,
                    MINIMUM_TAT_MINUTES,
                    MINIMUM_PERDIEM_MINUTES,
                    MAXIMUM_PERDIEM_MINUTES,
                    UNPAID_CODES,
                    domesticAirportSet,
                    airportMap,
                    periodStart,
                    periodEnd,
                };

        
                const total = crewSets.length;
                for (const [idx, member] of crewSets.entries()) {
                    if (abortCalculateRef.current) break;

                    if (idx % 50 === 0) {
                        setCalcProgress(Math.round((idx / total) * 100));
                        await new Promise(resolve => setTimeout(resolve, 0));
                    }

                    const rostersByCrew = rostersByCrewMap.get(member.crewCode) || [];

                    const flyingDaysMap = new Map();
                    const timesheetDaysMap = new Map();
                    const unpaidDaysMap = new Map();
                    
                    const bhThresholds = [20, 30, 45];
                    const accumulatedBlockHoursRef = { value: 0 };

                    const sectorTypes = [...new Set([...airportPairMap.values()].sort())];
                    sectorTypes.push("Default");

                    const overStats = Object.fromEntries(
                        bhThresholds.flatMap(th => {
                            const base = [
                                [`sectorsOver${th}`, 0],
                                [`sectorsUnder${th}`, 0]
                            ];

                            const perType = sectorTypes.flatMap(type => [
                                [`sectorsOver${th}Type_${type}`, 0],
                                [`sectorsUnder${th}Type_${type}`, 0]
                            ]);

                            return [...base, ...perType];
                        })
                    );
                    
                    const paxStats = { 
                        dhdTime: 0, 
                        dhdSectors: 0, 
                        posTime: 0, 
                        posSectors: 0 
                    };  
                    let accumulatedPayableHours = 0;
                    let excessLineInstructorTime = 0;
                    let pre = {};
                    
                    const processedRostersByCrew = rostersByCrew.map((cur, index) => {  
                        if (cur.pax) {
                            cur.paxType = determinePaxType(rostersByCrew, cur, index, POSITIONING_RULE);
                            if (cur.paxType) {
                                accumulatePaxStats(cur, startDate, endDate, paxStats);
                            }
                        } else {
                            accumulateOverStats(
                                cur,
                                startDate,
                                endDate,
                                overStats,
                                bhThresholds,
                                accumulatedBlockHoursRef,
                                airportPairMap
                            );
                        }
                                    
                        if (cur.sector) {
                            if (pre && pre.sector) {
                                calcPerdiem(pre, cur, perdiemConfig, addNote);
                            }

                            addFlyingDays(cur, periodStart, periodEnd, MINIMUM_OVERLAP_WORKDAY_MINUTES, DEBRIEFING_MINUTES, flyingDaysMap);                    
                        } else {
                            mapScheduleCodes(cur, { timesheetDaysMap, unpaidDaysMap }, periodStart, periodEnd, crewScheduleCodeMap);
                        }
                        
                        ({ 
                            accumulatedPayableHours, 
                            excessLineInstructorTime 
                        } = accumulateLineInstructorTime({
                            cur,
                            member,
                            startDate,
                            endDate,
                            accumulatedPayableHours,
                            excessLineInstructorTime,
                            fcRanks,
                            FLIGHT_CREW_EXCESS_BLOCK_HOURS,
                            CABIN_CREW_EXCESS_BLOCK_HOURS,
                            INSTRUCTOR_LINE_TRAINING_CODES
                        }));
                        
                                                    
                        pre = cur //Assign pre = cur
                        return cur;
                    });                         
        
                    const { 
                        bhOnDayOff, 
                        sectorsOnDayOff, 
                        lineInstructorBHOnDayOff, 
                        lineInstructorSectorsOnDayOff, 
                        tripCodeOnDayOff 
                    } = calcDutyOnDayOff(
                        processedRostersByCrew,
                        periodStart,
                        periodEnd,
                        crewScheduleCodes,
                        INSTRUCTOR_LINE_TRAINING_CODES,
                    );

                    const workingDatesMap = new Map();
                    const allCrewCodes = new Set([
                        ...flyingDaysMap.keys(),
                        ...timesheetDaysMap.keys()
                    ]);

                    for (const crewCode of allCrewCodes) {
                        const combined = new Map();

                        const mergeDays = (sourceMap) => {
                            if (!sourceMap.has(crewCode)) return;
                            for (const [day, info] of sourceMap.get(crewCode).entries()) {
                                const existing = combined.get(day);
                                if (!existing || existing.workingDays < info.workingDays) {
                                    combined.set(day, { workingDays: info.workingDays });
                                }
                            }
                        };

                        mergeDays(flyingDaysMap);
                        mergeDays(timesheetDaysMap);

                        // Kết quả cuối cùng là set các ngày có workingDays cao nhất
                        workingDatesMap.set(
                            crewCode,
                            new Map([...combined.entries()].sort()) // giữ Map ngày -> workingDays
                        );
                    }
                    
                    
                    const sliceStart = enabledFieldIndexes.length + 25; // Calculated fields from paxTime
                    const slicedColumnsSet = new Set(processedRostersByCrew.flatMap(row => Object.keys(row).slice(sliceStart)));
                    const columns = Array.from(slicedColumnsSet);

                    let dutyColumns = calcDutyColumns(
                        processedRostersByCrew, 
                        columns, 
                        startDate, 
                        endDate, 
                        atdDate
                    );

                    const flyingOnHolidays = calcFlyingOnHolidays(
                        processedRostersByCrew,
                        holidayDateSet,
                        atdDate,
                        ataDate,
                        periodStart,
                        periodEnd,
                    );

                    const cm = crewMemberMap.get(member.crewCode);
                    const email = cm?.email || '';
                    const division = cm?.division || '';
                    const department = cm?.department || '';
                    const unit = cm?.unit || '';
                    const agency = cm?.agency || '';
                    const isTerminated = cm?.isTerminated || false;
                    const jobGradeCode = cm?.jobGradeCode || '';
                    const isNetIncome = cm?.isNetIncome ?? false;
                    const insuranceDayCalculationType = cm?.insuranceDayCalculationType || '';
                                            
                    const workingDates = workingDatesMap.get(member.crewCode) || new Set();
                    const dates = Object.fromEntries(
                        [...workingDates].map(([date, obj]) => [date, obj.workingDays])
                    );
                    const contribution =  contributionMap.get(member.contributionObject) || {};
                    
                    // const unpaidDates =  unpaidDaysMap.get(member.crewCode) || [];
                    let activeJobs = cm?.activeJobs;
                    activeJobs = addDaysCount(activeJobs, workingDates, "workingDays");
                    // activeJobs = addDaysCount(activeJobs, unpaidDates, "unpaidDays");                        
                    
                    const jobTitles = cm?.activeJobTitles.join(', ') || '';
                    const employmentStartDate = cm?.employmentStartDate ? new Date(cm.employmentStartDate) : null;
                    const employmentEndDate = cm?.employmentEndDate ? new Date(cm.employmentEndDate) : null;
                    const activeDays = calcActiveDays(employmentStartDate, employmentEndDate, startDate, endDate);
                    
                    const { 
                        cumulativeSickCountByCrew, 
                        lastSickCountByCrew, 
                        currentSickCountByCrew 
                    } = crewSicks;
                    const cumulativeSickDays = cumulativeSickCountByCrew[member.crewCode] || 0;
                    const lastSickDays = lastSickCountByCrew[member.crewCode] || 0;
                    const currentSickDays = currentSickCountByCrew[member.crewCode] || 0;
                    const overSickDays = cumulativeSickDays > MAXIMUM_PAYABLE_SICK_DAYS 
                        ? cumulativeSickDays - Math.max(lastSickDays, MAXIMUM_PAYABLE_SICK_DAYS)
                        : 0;
                        
                    const unpaidDays = dutyColumns?.unpaidDays || 0;
                    const paidDays = activeDays < unpaidDays 
                        ? activeDays 
                        : Math.max(activeDays - unpaidDays, 0);
                    
                    const flyingDays = flyingDaysMap.get(member.crewCode)?.size || 0;
                    const workingDays = workingDates
                        ? Array.from(workingDates.values()).reduce((sum, { workingDays }) => sum + (workingDays || 0), 0)
                        : 0;
                    const { nonWorkingDays } = countWeekendDays(startDate, endDate);  
                    const standardWorkingDays = totalDays - nonWorkingDays;
                    const extraWorkingDays = Math.max(0, workingDays - standardWorkingDays);
                    
                    const reserveDays = Number(dutyColumns.RSV ?? 0) + Number(dutyColumns.RESV ?? 0);
                    const INSURANCE_DAY_CALCULATION_MAP = {
                        paidDays: paidDays,
                        workingDays: Number(workingDays) + Number(reserveDays),
                    };
                    const insuranceDays = INSURANCE_DAY_CALCULATION_MAP[insuranceDayCalculationType] ?? 0;                        
                    const hasContributions = (standardWorkingDays - insuranceDays) < MINIMUM_INSURANCE_DAYS;
                    
                    const personalDeduction_Amount = personalDeduction_Unit;
                    const dependentCount = countDependents(cm?.dependents, startDate, endDate);
                    const dependentDeduction_Amount = dependentDeduction_Unit * dependentCount;
                    
                    let excessStdHours = 0;
                    if (fcRanks.includes(member.crewRank)) {
                        excessStdHours = Math.max(0, dutyColumns["payableBlockTime"] - FLIGHT_CREW_EXCESS_BLOCK_HOURS);
                    } else if (ccRanks.includes(member.crewRank)) {
                        excessStdHours = Math.max(0, dutyColumns["payableBlockTime"] - CABIN_CREW_EXCESS_BLOCK_HOURS);
                    }

                    const kpiRate = crewKpiMap.get(member.crewCode)?.kpiRate ?? 0;

                    dutyColumns = {
                        ...paxStats,
                        ...overStats,
                        ...Object.fromEntries(
                            Object.entries(dutyColumns).flatMap(([key, value]) => {
                                if (key === "payableSectors") {
                                    return [
                                        [key, value],
                                        ["excessStdHours", excessStdHours],
                                        ["excessLineInstructorTime", excessLineInstructorTime],
                                    ];
                                }

                                if (key === "paidDayOffs") {
                                    return [
                                        [key, value],
                                        ["bhOnDayOff", bhOnDayOff],
                                        ["sectorsOnDayOff", sectorsOnDayOff],
                                        ["lineInstructorBHOnDayOff", lineInstructorBHOnDayOff],
                                        ["lineInstructorSectorsOnDayOff", lineInstructorSectorsOnDayOff],    
                                        ["cumulativeSickDays", cumulativeSickDays],
                                        ["lastSickDays", lastSickDays],                                    
                                        ["currentSickDays", currentSickDays],    
                                        ["overSickDays", overSickDays],
                                        ["totalDays", totalDays],
                                        ["activeDays", activeDays],                                        
                                    ];
                                }

                                if (key === "unpaidDays") {
                                    return [
                                        [key, value],    
                                        ["paidDays", paidDays],
                                        ["flyingDays", flyingDays],
                                        ["workingDays", workingDays],
                                        ["nonWorkingDays", nonWorkingDays],
                                        ["standardWorkingDays", standardWorkingDays],
                                        ["extraWorkingDays", extraWorkingDays],
                                        ["insuranceDays", insuranceDays],
                                        ["hasContributions", hasContributions],
                                        ["dependentCount", dependentCount],
                                        ["kpiRate", kpiRate],                                    
                                        ...Object.entries(tripCodeOnDayOff)
                                    ];
                                }

                                return [[key, value]];
                            })
                        ),
                    };

                    let dutyColumnsByJob = activeJobs?.map((job, index, arr) => {
                        const { start, end } = job;
                        if (!start || !end) return null;

                        const isLast = index === arr.length - 1;

                        accumulatedBlockHoursRef.value = 0;
                        const overByJob = Object.fromEntries(
                            bhThresholds.flatMap(th => {
                                const base = [
                                    [`sectorsOver${th}`, 0],
                                    [`sectorsUnder${th}`, 0]
                                ];

                                const perType = sectorTypes.flatMap(type => [
                                    [`sectorsOver${th}Type_${type}`, 0],
                                    [`sectorsUnder${th}Type_${type}`, 0]
                                ]);

                                return [...base, ...perType];
                            })
                        );
                        const paxByJob = { 
                            dhdTime: 0, 
                            dhdSectors: 0, 
                            posTime: 0, 
                            posSectors: 0 
                        };
                        accumulatedPayableHours = 0;
                        excessLineInstructorTime = 0;
                        processedRostersByCrew.forEach((cur) => {
                            if (cur.paxType) {
                                accumulatePaxStats(cur, start, end, paxByJob);
                            } else {
                                accumulateOverStats(
                                    cur,
                                    start,
                                    end,
                                    overByJob,
                                    bhThresholds,
                                    accumulatedBlockHoursRef,
                                    airportPairMap
                                );
                            }

                            ({ accumulatedPayableHours, excessLineInstructorTime } = accumulateLineInstructorTime({
                                cur,
                                member,
                                start,
                                end,
                                excessLineInstructorTime,
                                fcRanks,
                                FLIGHT_CREW_EXCESS_BLOCK_HOURS,
                                CABIN_CREW_EXCESS_BLOCK_HOURS,
                                INSTRUCTOR_LINE_TRAINING_CODES
                            }));
                        });

                        const { 
                            bhOnDayOff, 
                            sectorsOnDayOff, 
                            lineInstructorBHOnDayOff, 
                            lineInstructorSectorsOnDayOff, 
                            tripCodeOnDayOff 
                        } = calcDutyOnDayOff(
                            processedRostersByCrew,
                            start,
                            end,
                            crewScheduleCodes,
                            INSTRUCTOR_LINE_TRAINING_CODES,
                        );

                        const jobDutyColumns = calcDutyColumns(processedRostersByCrew, columns, start, end, atdDate);
                        const mergedColumns = {
                            ...job,
                            ...paxByJob,
                            ...overByJob,
                            excessLineInstructorTime,
                            bhOnDayOff, 
                            sectorsOnDayOff, 
                            lineInstructorBHOnDayOff, 
                            lineInstructorSectorsOnDayOff, 
                            ...tripCodeOnDayOff,
                            ...jobDutyColumns,      
                            ...(isLast ? { extraWorkingDays } : {}),                  
                        };

                        const paidDays = mergedColumns?.activeDays - (mergedColumns?.unpaidDays || 0);
                        const { nonWorkingDays } = countWeekendDays(start, end);
                        // const standardWorkingDays = mergedColumns?.activeDays - nonWorkingDays;

                        return {
                            ...mergedColumns,
                            totalDays,
                            paidDays,
                            nonWorkingDays, 
                            standardWorkingDays,
                            insuranceDays,
                            hasContributions,
                            dependentCount,
                            kpiRate,
                        };
                    }).filter(Boolean);

                    // const { breakdownUnit, breakdownQuantity, breakdownAmount, incomeAmount } = calcIncomeBreakdown(cm.payables, dutyColumns, isTerminated);
                    const travelPerdiem_Amount = travelAuthorizationFormMap.get(String(member.crewCode))?.allowanceAmount || 0;
                    const incomeAdjustment = incomeAdjustmentMap.get(String(member.crewCode));
                    const incomeAdjustment_Amount = incomeAdjustment?.amount || 0;
                    const isNonTaxableIncomeAdjustment = incomeAdjustment?.isNonTaxableIncome || false;

                    let income_Amount = 0;
                    let contributoryIncome_Amount = 0;
                    const contributoryCostItems = costItems?.filter(item => item.isContributoryIncome);
                    let nonTaxableIncome_Amount = 0;
                    const nonTaxableCostItems = costItems?.filter(item => item.isNonTaxableIncome);

                    dutyColumnsByJob = dutyColumnsByJob?.map((job) => {
                        const activeJobTitles = job.jobTitle;
                        const payables = buildPayables(costItems, activeJobTitles);

                        const { 
                            breakdownUnit, 
                            breakdownQuantity, 
                            breakdownAmount, 
                            incomeAmount 
                        } = calcIncomeBreakdown(payables, job, isTerminated);
                        income_Amount += incomeAmount;

                        contributoryCostItems?.forEach(item => {
                            contributoryIncome_Amount += breakdownAmount[`${item.costItemCode}_Amount`] || 0;
                        });

                        nonTaxableCostItems?.forEach(item => {
                            nonTaxableIncome_Amount += breakdownAmount[`${item.costItemCode}_Amount`] || 0;
                        });

                        
                        return {
                            ...job,
                            ...breakdownUnit,
                            ...breakdownQuantity,
                            ...breakdownAmount, 
                            incomeAmount,
                        }
                    })     

                    const totalIncome_Amount = income_Amount + travelPerdiem_Amount + incomeAdjustment_Amount;

                    nonTaxableIncome_Amount = nonTaxableIncome_Amount + travelPerdiem_Amount;
                    if (isNonTaxableIncomeAdjustment === false) {
                        nonTaxableIncome_Amount += incomeAdjustment_Amount;
                    }

                    const familyDeduction_Amount = personalDeduction_Amount + dependentDeduction_Amount;                            
                    
                    const { 
                        contributionBreakdown,
                        employeeContribution_Amount,
                        employerContribution_Amount,         
                    } = calcContributionBreakdown(contribution, contributoryIncome_Amount, hasContributions);
                    
                    const employeeTradeUnion_Amount = contributionBreakdown?.TUD?.employeeAmount || 0;
                    const convertedIncome_Amount = isNetIncome 
                        ? Math.max(totalIncome_Amount - nonTaxableIncome_Amount - familyDeduction_Amount + employeeTradeUnion_Amount, 0)
                        : Math.max(totalIncome_Amount - nonTaxableIncome_Amount, 0);
                    
                    const incomeBeforeTax_Amount = isNetIncome 
                        ? calcIncomeBeforeTax(convertedIncome_Amount)
                        : Math.max(convertedIncome_Amount - familyDeduction_Amount - (employeeContribution_Amount - employeeTradeUnion_Amount), 0);
                    
                    const personalIncomeTax_Amount = calcVietnamProgressiveTax(incomeBeforeTax_Amount);
                    
                    const grossIncome_Amount = isNetIncome 
                        ? Math.max(totalIncome_Amount + personalIncomeTax_Amount + employeeContribution_Amount, 0)
                        : totalIncome_Amount;

                    const taxableIncome_Amount = Math.max(grossIncome_Amount - nonTaxableIncome_Amount, 0);
                    
                    const netIncome_Amount = isNetIncome 
                        ? totalIncome_Amount
                        : Math.max(totalIncome_Amount - personalIncomeTax_Amount - employeeContribution_Amount, 0);
                                                
                    const generatedDaysColumns = generateDaysColumns(
                        processedRostersByCrew, 
                        startDate, 
                        endDate, 
                        MINIMUM_OVERLAP_WORKDAY_MINUTES,
                        DEBRIEFING_MINUTES,
                        CREW_PAYROLL_TIME_MODE,
                    );

                    const filteredRostersByCrew = filterArrayByPeriod(processedRostersByCrew, atdDate, periodStart, periodEnd)
                    timesheetDetails.push(...filteredRostersByCrew);        

                    timesheetSummary.push({
                        _id: timesheetSummary.length + 1,
                        crewCode: member.crewCode,
                        crewName: member.crewName,
                        post: member.crewRank,
                        base: member.crewBase,
                        email,
                        division,
                        department,
                        unit,
                        agency,
                        employmentStartDate,
                        employmentEndDate,
                        isTerminated,
                        jobGradeCode,
                        jobTitles,
                        activeJobs: dutyColumnsByJob,
                        // contributionRates,
                        // ...breakdownUnit,
                        // ...breakdownAmount,
                        contributoryIncome_Amount,
                        ...contributionBreakdown,
                        employeeContribution_Amount,
                        employerContribution_Amount,
                        travelPerdiem_Amount,
                        incomeAdjustment_Amount,
                        income_Amount,
                        totalIncome_Amount,                            
                        familyDeduction_Amount,
                        nonTaxableIncome_Amount,
                        convertedIncome_Amount,
                        incomeBeforeTax_Amount,
                        personalIncomeTax_Amount,
                        grossIncome_Amount,
                        taxableIncome_Amount,
                        netIncome_Amount,
                        insuranceDayCalculationType,
                        isNetIncome,
                        // taxableIncome,
                        // incomeAfterTax,
                        flyingOnHolidays,
                        ...dutyColumns,            
                        dates,                
                        ...generatedDaysColumns,
                    });
                }
                
                if (!abortCalculateRef.current) {
                    setPayrollDetails(timesheetDetails);
                    setPayrollSummary(timesheetSummary);
                    toast.success("Calculation completed.");
                }
            };

            await processTimesheet();
            if (!abortCalculateRef.current) setView('payrollSummary');
        } catch (error) {
            setError(error);
            toast.error(error.message || 'Calculation failed.');
        } finally {
            setIsCalculating(false);
        }
    }, [
        paramsMap, 
        columnConfig, 
        startDate, 
        endDate, 
        costItems, 
        airports, 
        airportPairs,
        fixedBlockTimes, 
        countries, 
        crewScheduleCodes, 
        crewMembers, 
        contributions,
        travelAuthorizationForms, 
        incomeAdjustments, 
        crewKpis, 
        crewSicks,
        holidays,
        fcRanks, 
        ccRanks, 
        crewRanksData, 
        rawData,
        pitConfig,
    ]);
        
    const handleCancelCalculate = useCallback(() => {
        abortCalculateRef.current = true;
        setIsCalculating(false);
        toast('Calculation cancelled.', { icon: '⚠️' });
    }, []);

    const handleSave = useCallback(() => {
        if (view === 'rawData') {
            toast.error("Switch to Payroll Details or Summary to save.");
            return;
        }
        const dataToSave = view === "payrollDetails" ? payrollDetails : payrollSummary;
        const dataType   = view === "payrollDetails" ? "details" : "summary";

        execSave(dataToSave, dataType, startDate, endDate);
    }, [
        startDate, 
        endDate, 
        view, 
        payrollDetails, 
        payrollSummary, 
        execSave
    ]);

    const handleExcelJSDownload = () => {        
        const formatCellForExcel = (value, depth = 0) => {
            if (value == null || value === "") return "";
            if (typeof value === "boolean") return value ? "Yes" : "No";
            // Date instance phải check trước typeof "object"
            if (value instanceof Date) return isNaN(value.getTime()) ? "" : format(value, "yyyy-MM-dd");
            if (typeof value === "number") return depth === 0 ? value : value;

            // Array
            if (Array.isArray(value)) {
                if (value.length === 0) return "";
                // Array of primitives → join comma
                if (value.every(v => v == null || typeof v !== "object")) {
                    return value.map(v => v == null ? "" : String(v)).join(", ");
                }
                // Array of objects → each item on its own line
                if (depth >= 2) return `[${value.length} items]`;
                return value
                    .map(item => item == null ? "" : formatCellForExcel(item, depth + 1))
                    .join("\n---\n");
            }

            // Object (plain)
            if (typeof value === "object") {
                const entries = Object.entries(value);
                if (entries.length === 0) return "";
                if (depth >= 2) return "{...}";
                const sep = depth === 0 ? "\n" : " | ";
                return entries
                    .map(([k, v]) => `${k}: ${formatCellForExcel(v, depth + 1)}`)
                    .join(sep);
            }

            // Numeric string → convert to number
            if (value !== "" && !isNaN(value)) return Number(value);

            // Date string (ISO format)
            if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
                const d = new Date(value);
                if (!isNaN(d.getTime())) return format(d, "yyyy-MM-dd");
            }

            return String(value);
        };

        const exportToExcel = async (data, fileName = `Timesheet`) => {
            try {
                // 1. Create a new workbook
                const workbook = new ExcelJS.Workbook();
                const worksheet = workbook.addWorksheet(fileName);

                // Add title row
                worksheet.addRow([fileName]);
                // worksheet.mergeCells("A1:Z1");
                worksheet.getCell("A1").alignment = { horizontal: "center", vertical: "middle" };
                worksheet.getCell("A1").font = { size: 16, bold: true };

                // Add subtitle row
                const fullName = user?.firstName && user?.lastName ? user?.lastName + ' ' + user?.firstName : 'Unknown';
                const createdDate = `Reported by: ${fullName}, at: ${new Date().toLocaleString()}`;
                worksheet.addRow([createdDate]);
                // worksheet.mergeCells("A2:Z2");
                worksheet.getCell("A2").alignment = { horizontal: "center", vertical: "middle" };
                worksheet.getCell("A2").font = { italic: true };

                // Add subtitle row
                const timingMode = `Time mode: ${timeMode}`;
                worksheet.addRow([timingMode]);
                // worksheet.mergeCells("A3:Z3");
                worksheet.getCell("A3").alignment = { horizontal: "center", vertical: "middle" };
                worksheet.getCell("A3").font = { italic: true };

                // Add an empty row
                worksheet.addRow([]);
                
                // 2. Define column headers             
                const excelColumns = columns.map(col => ({
                    header: col.headerName || col.field, // Use headerName if available, else use field
                    key: col.field, // The key must match the row data keys
                    width: Math.max(10, col.width / 8) // Auto-adjust width
                }));
                   
                worksheet.addRow(excelColumns.map(col => col.header));

                // 3. Add data rows
                data.forEach(row => {
                    const rowData = columns.map(col => formatCellForExcel(row[col.field]));
                    worksheet.addRow(rowData);
                });

                // 4. Apply styles (e.g., wrap text for certain columns)
                worksheet.eachRow((row, rowIndex) => {
                    row.eachCell((cell) => {
                        // Apply alignment and text wrapping
                        cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
                        
                        // Apply border styles
                        cell.border = {
                            top: { style: "thin" },
                            left: { style: "thin" },
                            bottom: { style: "thin" },
                            right: { style: "thin" },
                        };
                        
                        if (rowIndex === 5) {
                            // Style for headers
                            cell.font = { bold: true };
                            cell.fill = {
                                type: "pattern",
                                pattern: "solid",
                                fgColor: { argb: "FFFFCC" },
                            };
                        }
                    });
                });
                

                const getExcelColumnLetter = (colIndex) => {
                    let letter = "";
                    while (colIndex >= 0) {
                        letter = String.fromCharCode((colIndex % 26) + 65) + letter;
                        colIndex = Math.floor(colIndex / 26) - 1;
                    }
                    return letter;
                };
                const totalColumns = excelColumns.length;
                const lastColumnLetter = getExcelColumnLetter(totalColumns - 1);

                const header = `${fileName} ${format(startDate, 'ddMMMyyyy')} - ${format(endDate, 'ddMMMyyyy')}`;
                worksheet.getCell("A1").value = header;
                worksheet.mergeCells(`A1:${lastColumnLetter}1`);
                worksheet.mergeCells(`A2:${lastColumnLetter}2`);
                worksheet.mergeCells(`A3:${lastColumnLetter}3`);

                // 5. Generate and save the file
                const buffer = await workbook.xlsx.writeBuffer();
                saveAs(new Blob([buffer]), fileName); 
            } catch (error) {
                toast.error(error?.message || "Failed to export to Excel.");
            }
        };

        const dataToExport = filteredPayroll;
        if (!dataToExport || dataToExport.length === 0) {
            toast.warning("No data available to export.");
            return;
        }

                        
        // Add a timestamp to the filename
        const timestamp = new Date().toISOString().replace(/[-T:.Z]/g, "").slice(0, 14); // YYYYMMDDHHMMSS
        const fileName = `Timesheet_${timestamp}.xlsx`;

        exportToExcel(dataToExport, fileName);
    };
    
    const { baseOptions, postOptions } = useMemo(() => {
        if (!payrollSummary || payrollSummary.length === 0) {
            return { baseOptions: [], postOptions: [] };
        }

        const baseSet = new Set();
        const postSet = new Set();

        payrollSummary.forEach(member => {
            if (member.base) baseSet.add(member.base);
            if (member.post) postSet.add(member.post);
        });
     
        const baseOptions = Array.from(baseSet).sort();

        const postOptions = Array.from(postSet)
            .map(code => rankMap.get(code))
            .filter(Boolean)
            .sort((a, b) => a.rankPriority - b.rankPriority)
            .map(rank => rank.rankCode);

        return { baseOptions, postOptions };
    }, [rankMap, payrollSummary]);
        
    const formatDate = (date) => {
        if (!date) return null;
        return formatInTimeZone(new Date(date), "UTC", "dd/MM/yyyy");
    }
    
    const formatGeneralValue = useCallback((value, indent = 0) => {
        const spacing = " ".repeat(indent * 2);
        if (value == null) return "";

        // Ngày        
        if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}(T.*)?$/.test(value)) {
            const date = parseISO(value);
            if (isValid(date)) {
                if (value.endsWith("T00:00:00.000Z")) {
                    return formatDate(date);
                }

                const hours = date.getUTCHours();
                const minutes = date.getUTCMinutes();
                const seconds = date.getUTCSeconds();
                if (hours === 0 && minutes === 0 && seconds === 0) {
                    return formatDate(date);
                }
                return formatInTimeZone(date, "UTC", "HH:mm dd/MM/yyyy");
            }
        }

        // Ngày, Số, Boolean, Chuỗi
        if (value instanceof Date) return formatInTimeZone(value, "UTC", "dd/MM/yyyy");
        if (typeof value === "number") return value.toLocaleString("vi-VN");
        if (typeof value === "boolean")
            return value ? (
                <Check sx={{ color: theme.palette.success.main, fontSize: 16 }} />
            ) : (
                <Close sx={{ color: theme.palette.error.main, fontSize: 16 }} />
            );
        if (typeof value === "string") return value;

        // Mảng
        if (Array.isArray(value)) {
            if (value.length === 0) return "[]";
            if (value.every(v => typeof v === "string")) return value.join(", ");

            // Mảng object
            return (
                <Typography
                    variant="body2"
                    component="div"
                    sx={{ whiteSpace: "pre-line", fontFamily: "monospace" }}
                >
                    {value.map((item, idx) => {
                            if (typeof item === "object" && item !== null) {
                                return (
                                    `${spacing}- ` +
                                    Object.entries(item)
                                        .map(([k, v]) => `${k}: ${formatGeneralValue(v, indent + 1)}`)
                                        .join(", ")
                                );
                            };
                            return `${spacing}- ${formatGeneralValue(item, indent + 1)}`;
                        }).join("\n")
                    }
                </Typography>
            );
        }

        // Object
        if (typeof value === "object") {
            const entries = Object.entries(value);
            if (entries.length === 0) return "{}";

            return (
                <Typography
                    variant="body2"
                    component="div"
                    sx={{ whiteSpace: "pre-line", fontFamily: "monospace" }}
                >
                    {entries
                        .map(([k, v]) => `${spacing}${k}: ${formatGeneralValue(v, indent + 1)}`)
                        .join("\n")}
                </Typography>
            );
        }

        return String(value);
    }, [theme]);

    const handleViewCrewIncomeClick = useCallback(async (params) => {
        try {      
            setCrewIncome(params);
            setOpenCrewIncomeDialog(true)
        } catch (error) {
            setOpenCrewIncomeDialog(false);
        } 
    }, []);

    const columns = useMemo(() => {
        if (!filteredPayroll || filteredPayroll.length === 0) return [];

        // Single pass: collect all keys + max cell length per key
        const allKeys = new Set();
        const maxLengthPerKey = new Map();
        filteredPayroll.forEach(row => {
            Object.entries(row).forEach(([k, v]) => {
                allKeys.add(k);
                const len = v ? String(v).length : 0;
                if (len > (maxLengthPerKey.get(k) ?? 0)) maxLengthPerKey.set(k, len);
            });
        });

        return [
            {
                field: "no",
                headerName: "No",
                filterable: false,
                sortable: false,
                width: 60,
                renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
            },
            ...Array.from(allKeys).map((key) => {
                const maxCellLength = Math.max(key.length, maxLengthPerKey.get(key) ?? 0);

                const specialWidths = {
                    crewName: 250,
                    activeJobs: 900,
                    dates: 145,
                };
                const columnWidth = specialWidths[key] ?? Math.min(Math.max(maxCellLength * 8, 90), 200);
                
                const isNumber = filteredPayroll.every(
                    (row) =>
                        row[key] == null ||
                        typeof row[key] === "number" ||
                        (!isNaN(row[key]) && row[key] !== "" && typeof row[key] !== "boolean")
                );

                if (key === "crewName") {
                    return {
                        field: key,
                        headerName: capitalizeFirstLetter(key),
                        width: columnWidth,
                        align: "left",
                        renderCell: (params) => (
                            <Box
                                sx={{
                                    cursor: "pointer",
                                    '&:hover': {
                                        color: "text.primary",
                                        textDecoration: 'underline',
                                    },
                                }}
                                onClick={() => handleViewCrewIncomeClick(params.row)}
                            >
                                {formatGeneralValue(params.value)}
                            </Box>
                        ),
                    };
                }

                return {
                    field: key,
                    headerName: capitalizeFirstLetter(key),
                    editable: true,
                    width: columnWidth,
                    align: isNumber ? "right" : "left",
                    renderCell: (params) => {
                        return (
                            <Box
                                sx={{
                                    height: '100%',
                                    alignItems: 'flex-start',
                                    overflowY: 'auto',
                                    overflowX: 'hidden',
                                }}
                            >
                                {formatGeneralValue(params.value)}
                            </Box>
                        )
                    }                   
                };
            }),
        ];
    }, [
        filteredPayroll, 
        formatGeneralValue, 
        handleViewCrewIncomeClick
    ]);
                
    const hiddenColumns = {  
        _id: false,    
    };

    const nonMobileHiddenColumns = {
        _id: false,
    };
    
    const actionColumn = useMemo(() => {    
        const activeCount = [canView].filter(Boolean).length;
        if (activeCount === 0) return [];
    
        const baseWidth = 60 + activeCount * 25;
    
        return [
            {
                field: "action",
                headerName: "Action",
                width: baseWidth,
                filterable: false,      
                sortable: false,    
                renderCell: (params) => (
                    <FlexCenter
                        height="100%" 
                        gap={1}
                    >
                        <Tooltip title="View">
                            <IconButton
                                aria-label="View"
                                sx={{
                                    color: theme.palette.primary.dark,
                                    cursor: 'pointer',
                                    '&:hover': {
                                        color: theme.palette.green.deep,
                                    }
                                }}                              
                                onClick={() => {
                                    if (!canView) return;
                                    handleViewCrewRosters(params.row);
                                }}         
                            >
                                <Visibility />
                            </IconButton>
                        </Tooltip>          
                    </FlexCenter>
                ),
            }
        ]
    }, [
        canView, 
        theme.palette, 
        handleViewCrewRosters,
    ]);
    
    
    return (
        <Box className="main-container-box">                  
            <FlexBetween >
                <Header 
                    title="Crew Payroll" 
                    subtitle={`${loading ? 'Loading...' : 'List of Figures'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>  
                        {!isMobile && (
                            <Box display="flex" alignItems="center" gap={1}>
                                <Tooltip title="Days Offset" arrow>
                                    <span>
                                        <NumberControl
                                            min={1}
                                            max={31}
                                            value={daysOffset}
                                            onChange={(value) => {
                                                setDaysOffset(value); 
                                            }} 
                                            textFieldWidth={45}
                                        />
                                    </span>                                     
                                </Tooltip>       
                               
                                <Tooltip title="PIT config" arrow>
                                    <span>
                                        <StyledButton onClick={() => setOpenPitConfig(true)}>
                                            <ManageAccounts />
                                        </StyledButton>
                                    </span>
                                </Tooltip>
                                
                                <Tooltip title="Upload column config" arrow>
                                    <span>
                                        <StyledButton onClick={() => setOpenUploadConfig(true)}>
                                            <Settings />
                                        </StyledButton>
                                    </span>
                                </Tooltip>

                                {!fileName && (
                                    <Tooltip title="Upload job 147 text file" arrow>
                                        <span>
                                            <label
                                                htmlFor={canUpload ? "upload-file" : undefined}
                                                onClick={(e) => { if (!canUpload) e.preventDefault(); }}
                                            >
                                                <StyledButton component="span">
                                                    <FileUpload />
                                                </StyledButton>
                                            </label>
                                            <input
                                                id="upload-file"
                                                type="file"
                                                accept=".txt"
                                                style={{ display: 'none' }}
                                                onChange={(e) => {
                                                    handleJob147Upload(e);
                                                    logClientAction("UPLOAD_JOB147", { fileName: "CrewPayroll", format: "txt", startDate, endDate });
                                                }}
                                            />
                                        </span>
                                    </Tooltip>
                                )}

                                {fileName && (
                                    <Tooltip title={`Reset: ${fileName}`} arrow>
                                        <span>
                                            <StyledButton onClick={handleResetFile}>
                                                <Clear />
                                            </StyledButton>
                                        </span>
                                    </Tooltip>
                                )}                                                           
                            </Box>              
                        )} 
                       
                        <Tooltip title="Calculate payroll" arrow>
                            <span>
                                <StyledButton
                                    disabled={isCalculating}
                                    onClick={() => {
                                        if (rawData.length > 0) {
                                            if (canUpload) {
                                                handleCalculate();
                                            } else {
                                                toast.error("Please upload Job147 file before calculation.");
                                                return;
                                            }
                                        } else {
                                            if (canCalculate) {
                                                handleCrewPayroll();
                                            }
                                        }
                                    }}
                                >
                                    <Calculate />
                                </StyledButton>
                            </span>
                        </Tooltip>    

                        <ToggleButtonGroup
                            aria-label="View toggle"
                            value={view}
                            exclusive
                            onChange={(_, newView) => {
                                if (newView) setView(newView);
                            }}
                        >
                            {[
                                { value: "rawData", label: "Raw Data", icon: <ViewList />, disabled: rawData.length === 0 },
                                { value: "payrollDetails", label: "Payroll Details", icon: <ViewSidebar /> },
                                { value: "payrollSummary", label: "Payroll Summary", icon: <ViewModule /> },
                            ]
                            .filter(item => !item.disabled)
                            .map(({ value, label, icon, disabled }) => (
                                <Tooltip key={value} title={label} arrow>
                                    <span>
                                        <ToggleButton
                                            aria-label={value}
                                            size="small"
                                            value={value}
                                            disabled={!!disabled}
                                        >
                                            {icon}
                                        </ToggleButton>
                                    </span>
                                </Tooltip>
                            ))}
                        </ToggleButtonGroup>
                        
                        <Tooltip title="Download excel file" arrow>
                            <span>
                                <StyledButton 
                                    disabled={payrollSummary.length === 0}
                                    onClick={() => {
                                        handleExcelJSDownload();
                                        logClientAction("EXPORT_REPORT", { fileName: "CrewPayroll", format: "Excel", startDate, endDate });
                                    }}
                                >
                                    <Download />
                                </StyledButton>
                            </span>
                        </Tooltip>

                        {!isMobile && (
                            <Tooltip title="Save" arrow>
                                <span>
                                    <StyledButton
                                        disabled={isSaving || (view === 'payrollDetails' && payrollDetails.length === 0) || (view === 'payrollSummary' && payrollSummary.length === 0)}
                                        onClick={() => {
                                            if (!canSave) return;
                                            handleSave();
                                        }}
                                    >
                                        <Save />
                                    </StyledButton>
                                </span>
                            </Tooltip>
                        )}

                        <FilterButton
                            filters={filters}
                            defaultFilters={defaultFilters}
                            onClick={handleFilterOpen}
                        />               
                    </Box>
                )}     
            </FlexBetween>
            <ProgressModal
                open={isCalculating}
                onClose={handleCancelCalculate}
                title="Calculating ..."
                subtitle="Please wait, this may take a few seconds."
                severity="info"
                loading
                progress={calcProgress}
                cancelText="Cancel"
                maxWidth="xs"
            />
            <ProgressModal
                open={isSaving}
                onClose={handleCancelSave}
                title="Saving ..."
                severity="info"
                loading
                progress={saveProgress}
                cancelText="Cancel"
                maxWidth="xs"
            />
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box
                    display="grid"
                    gap={2}
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                >
                    <Box
                        height={LAYOUT_HEIGHT}  
                        sx={{ gridColumn: "span 12", overflowY: 'auto' }}
                        onKeyDown={handleKeyDown}
                        tabIndex={0}
                    >                            
                        <DataGridComponent
                            rows={filteredPayroll || []}
                            columns={columns.concat(actionColumn)}
                            loading={loading}
                            props={{
                                initialState: {
                                    pagination: { paginationModel: { page: 0, pageSize: 100 } },
                                    columns: { columnVisibilityModel: !isDesktop ? hiddenColumns : nonMobileHiddenColumns },
                                },
                                disableSelectionOnClick: true,
                                processRowUpdate: handleRowUpdate,
                                onProcessRowUpdateError: handleRowUpdateError,
                                onRowSelectionModelChange: (newRowSelectionModel) => setRowSelectionModel(newRowSelectionModel),
                                rowSelectionModel: rowSelectionModel,
                            }}
                        />                        
                    </Box>
                </Box>
            )}

            <ScrollToTop />
            {isFilterDialogOpen &&
                <CrewFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    postOptions={postOptions}
                    baseOptions={baseOptions}
                    currentFilters={filters}
                />
            }

            {openRosterDetailsDialog &&
                <RosterDetailsDialog 
                    open={openRosterDetailsDialog}
                    onClose={handleCloseRosterDetailsDialog}
                    crewCode={selectedCrewCode}
                    crewName={selectedCrewName}
                    crewRosters={crewRosters}
                />
            }
                
            {openCrewIncomeDialog &&
                <CrewIncomeDialog 
                    open={openCrewIncomeDialog} 
                    onClose={() => setOpenCrewIncomeDialog(false)} 
                    data={crewIncome} 
                />
            }

            {openUploadConfig && (
                <UploadConfigDialog
                    open={openUploadConfig}
                    onClose={() => setOpenUploadConfig(false)}
                    onSave={handleSaveColumnConfig}
                    initialConfig={columnConfig}
                />
            )}

            {openPitConfig && (
                <PitConfigDialog
                    open={openPitConfig}
                    onClose={() => setOpenPitConfig(false)}
                    initialConfig={{
                        validSince:         pitConfig.validSince,
                        validUntil:         pitConfig.validUntil,
                        personalDeduction:  pitConfig.personalDeduction,
                        dependentDeduction: pitConfig.dependentDeduction,
                        brackets:           pitConfig.brackets.map(b => ({ ...b, max: b.max === Infinity ? null : b.max })),
                        rules:              pitConfig.rules,
                    }}
                    onSaved={handleSavePitConfig}
                />
            )}
        </Box>
    );
};

export default CrewPayroll;
